import { useState, useEffect } from 'react';

interface FetchResult<T> {
    result: T[] | null;
    isLoading: boolean;
    errorMsg: string | null;
    currentPage: number;
    totalPages: number;
    totalResults: number;
    doSearch: (text: string) => void;
    nextPage: () => void;
    prevPage: () => void;
    goToPage: (value: number) => void;
    reset: () => void;
}

interface UseElasticSearchProps {
    debounceTime?: number;
    boostIndex?: string[];
    urlElastic: string;
    apikeyElastic: string;
    selectedFields?: string[] | null;
    limitPerPage?: number;
    currentPage?: number;
    onNoResults?: () => void;
}

/**
 * El hook `useElasticSearch` es una utilidad creada para facilitar la conexión y búsqueda de datos en ElasticSearch. Permite manejar búsquedas paginadas y mejorar la experiencia de usuario mediante funcionalidades como debounce y navegación entre páginas.

 * @param {UseElasticSearchProps}  
 * - `debounceTime` : Tiempo en milisegundos que el hook espera antes de ejecutar la búsqueda, útil para evitar llamadas excesivas al servidor mientras el usuario escribe.
 * - `boostIndex` : Índice que ajusta la relevancia de los resultados al realizar búsquedas.
 * - `urlElastic` : URL del servidor de Elasticsearch al que se conectará el hook.
 * - `apikeyElasic` : Clave de API necesaria para autenticar las solicitudes al servidor de Elasticsearch.
 * - `selectedFields` : Campos específicos que se desean incluir en la búsqueda. Si es null, se buscarán todos los campos disponibles.
 * - `limitPerPage` : Número máximo de resultados que se mostrarán por página.
 * - `currentPage` : Página inicial que se utilizará al realizar la búsqueda.
 * @returns 
 * El hook `useElasticSearch` devuelve un objeto con las siguientes propiedades:
 * - `result` : Array con los resultados de la búsqueda. Es null si no hay resultados o la búsqueda aún no se ha realizado.
 * - `isLoading` : Booleano que indica si la búsqueda está en progreso.
 * - `errorMsg` : Mensaje de error si ocurre algún problema durante la búsqueda.
 * - `currentPage` : Página actual de los resultados, devuelta como currentPageSearch.
 * - `totalPages` : Número total de páginas disponibles según los resultados de la búsqueda.
 * - `totalResults` : Número total de resultados obtenidos de la búsqueda.
 * - `doSearch` : Función que inicia una búsqueda al pasar el texto que se desea buscar.
 * - `nextPage` : Función para avanzar a la siguiente página de resultados.
 * - `prevPage` : Función para retroceder a la página anterior.
 * - `goToPage` : Función para ir a una página específica de los resultados.
 * - `reset` : Reinicia el estado del hook, eliminando los resultados actuales, errores, paginación, etc.
 */
const useElasticSearch = <T>({
    debounceTime = 500,
    boostIndex = [],
    urlElastic,
    apikeyElastic,
    selectedFields = null,
    limitPerPage = 10,
    currentPage = 1,
    onNoResults,
}: UseElasticSearchProps): FetchResult<T> => {
    const [searchText, setSearchText] = useState<string>('');
    const [currentPageSearch, setCurrentPageSearch] =
        useState<number>(currentPage);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [result, setResult] = useState<T[] | null>(null);
    const [indexBoostList, setIndexBoostList] = useState<
        { [key: string]: number }[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const reset = () => {
        setResult(null);
        setIsLoading(false);
        setErrorMsg(null);
        setSearchText('');
        setCurrentPageSearch(1);
        setTotalPages(0);
        setTotalResults(0);
    };

    const nextPage = () => {
        setCurrentPageSearch(currentPageSearch + 1);
    };

    const prevPage = () => {
        if (currentPageSearch > 1) setCurrentPageSearch(currentPageSearch - 1);
    };

    const goToPage = (value: number) => {
        setCurrentPageSearch(value);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            if (!searchText.trim() || searchText.length <= 2) {
                setResult(null);
                return;
            }

            const fetchData = async () => {
                setIsLoading(true);
                setErrorMsg(null);

                try {
                    const response = await fetch(urlElastic, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `ApiKey ${apikeyElastic}`,
                        },
                        body: JSON.stringify({
                            from: (currentPageSearch - 1) * limitPerPage,
                            size: limitPerPage,
                            query: {
                                query_string: {
                                    query: `${searchText}~`,
                                    ...(selectedFields && {
                                        fields: selectedFields,
                                    }),
                                },
                            },
                            indices_boost: indexBoostList,
                        }),
                    });

                    if (!response.ok) {
                        throw new Error(`Error: ${response.statusText}`);
                    }

                    const result = await response.json();
                    if (result.hits.total.value === 0) {
                        setResult([]);
                        setTotalResults(0);
                        setTotalPages(0);
                        onNoResults && onNoResults();
                        return;
                    }

                    setResult(
                        result.hits.hits.map((item: any) => item._source)
                    );
                    setTotalResults(result.hits.total.value);
                    setTotalPages(
                        Math.ceil(result.hits.total.value / limitPerPage)
                    );
                } catch (err) {
                    if (err instanceof Error) {
                        setErrorMsg(err.message);
                    } else {
                        setErrorMsg('An unknown error occurred');
                    }
                } finally {
                    setIsLoading(false);
                }
            };

            fetchData();
        }, debounceTime);

        return () => clearTimeout(handler);
    }, [searchText, currentPageSearch]);

    useEffect(() => {
        const boost = boostIndex.map((index) => ({
            [index]: 10,
        }));

        boost.push({ '*': 1 });
        setIndexBoostList(boost);
    }, []);

    return {
        result,
        isLoading,
        errorMsg,
        doSearch: setSearchText,
        reset,
        nextPage,
        prevPage,
        totalPages,
        goToPage,
        currentPage: currentPageSearch,
        totalResults,
    };
};

export default useElasticSearch;
